<template>
  <div>

    <!-- Page Header -->
    <div class="page-header">
      <div>
        <h5 class="page-title">Attendance</h5>
        <p class="page-sub">Track employee clock-in / clock-out and daily status</p>
      </div>
      <div class="page-actions">
        <button class="btn-outline" @click="showMarkAbsentModal = true">
          <font-awesome-icon icon="user-slash" /> Mark Absent
        </button>
        <div class="export-wrap">
          <button class="btn-export" @click="exportOpen = !exportOpen">
            <font-awesome-icon icon="download" /> Export <font-awesome-icon icon="chevron-down" />
          </button>
          <div v-if="exportOpen" class="export-menu">
            <button class="export-item" @click="openExportModal('csv'); exportOpen = false">
              <font-awesome-icon icon="file-csv" /> Export CSV
            </button>
            <button class="export-item" @click="openExportModal('pdf'); exportOpen = false">
              <font-awesome-icon icon="file-pdf" /> Export PDF
            </button>
          </div>
        </div>
        <button class="btn-primary-action" @click="openSummary">
          <font-awesome-icon icon="chart-bar" /> Summary Report
        </button>
      </div>
    </div>

    <!-- Filters -->
    <div class="filter-bar">
      <input type="date" class="filter-input" v-model="filters.date_from" placeholder="From" />
      <input type="date" class="filter-input" v-model="filters.date_to" placeholder="To" />
      <select class="filter-select" v-model="filters.status">
        <option value="">All Statuses</option>
        <option value="present">Present</option>
        <option value="absent">Absent</option>
        <option value="late">Late</option>
        <option value="early_departure">Early Departure</option>
        <option value="half_day">Half Day</option>
        <option value="on_leave">On Leave</option>
        <option value="holiday">Holiday</option>
      </select>
      <input type="text" class="filter-input search-input" v-model="filters.search" placeholder="Search employee..." />
      <div class="per-page-wrap">
        <label class="per-page-label">Per page:</label>
        <select class="per-page-select" v-model.number="filters.length" @change="filters.page = 1; fetchAttendance()">
          <option v-for="n in [5,10,20,50,100,200,500,1000]" :key="n" :value="n">{{ n }}</option>
        </select>
      </div>
      <button class="btn-primary-action" @click="fetchAttendance">
        <font-awesome-icon icon="search" /> Search
      </button>
    </div>

    <!-- Skeleton -->
    <div v-if="loading" class="sk-table">
      <div class="sk-row-head"></div>
      <div v-for="n in 8" :key="n" class="sk-row"></div>
    </div>

    <!-- Table -->
    <div v-else class="table-card">
      <table class="data-table">
        <thead>
          <tr>
            <th>Employee</th>
            <th>Date</th>
            <th>Clock In</th>
            <th>Clock Out</th>
            <th>Hours</th>
            <th>Overtime</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="log in logs" :key="log.id">
            <td>
              <div class="emp-cell">
                <span class="emp-name">{{ log.employee?.first_name }} {{ log.employee?.last_name }}</span>
                <span class="emp-no">{{ log.employee?.employee_no }}</span>
              </div>
            </td>
            <td>{{ fmtDate(log.work_date) }}</td>
            <td>{{ fmtTime(log.clock_in) || '—' }}</td>
            <td>{{ fmtTime(log.clock_out) || '—' }}</td>
            <td>{{ log.worked_hours != null ? log.worked_hours + 'h' : '—' }}</td>
            <td>{{ log.overtime_hours > 0 ? log.overtime_hours + 'h' : '—' }}</td>
            <td><span class="status-chip" :class="statusClass(log.status)">{{ statusLabel(log.status) }}</span></td>
            <td>
              <button class="btn-icon" @click="openEdit(log)" title="Edit">
                <font-awesome-icon icon="pen" />
              </button>
              <button class="btn-icon" @click="openDetail(log)" title="Audit trail">
                <font-awesome-icon icon="history" />
              </button>
            </td>
          </tr>
          <tr v-if="!logs.length">
            <td colspan="8" class="empty-cell">No attendance records found</td>
          </tr>
        </tbody>
      </table>

      <!-- Pagination -->
      <div class="pagination-bar" v-if="pagination && pagination.last_page > 1">
        <span class="pagination-info">
          Showing {{ (pagination.current_page - 1) * filters.length + 1 }}–{{ Math.min(pagination.current_page * filters.length, pagination.total) }} of {{ pagination.total }}
        </span>
        <div class="pagination-controls">
          <button class="pg-btn" :disabled="pagination.current_page === 1" @click="goPage(1)">«</button>
          <button class="pg-btn" :disabled="pagination.current_page === 1" @click="goPage(pagination.current_page - 1)">‹</button>
          <div class="pg-pages">
            <button v-for="p in pageRange" :key="p" class="pg-num" :class="{ 'pg-num--active': p === pagination.current_page }" @click="goPage(p)">{{ p }}</button>
          </div>
          <button class="pg-btn" :disabled="pagination.current_page === pagination.last_page" @click="goPage(pagination.current_page + 1)">›</button>
          <button class="pg-btn" :disabled="pagination.current_page === pagination.last_page" @click="goPage(pagination.last_page)">»</button>
        </div>
      </div>
    </div>

    <!-- ══ Edit Attendance Modal ══ -->
    <div class="modal-overlay" v-if="editModal.open" @click.self="editModal.open = false">
      <div class="modal-box">
        <div class="modal-head">
          <p class="modal-title">Edit Attendance Record</p>
          <button class="modal-close" @click="editModal.open = false"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="modal-body">
          <div class="form-row">
            <label class="form-label">Clock In</label>
            <input type="datetime-local" class="form-input" v-model="editModal.clock_in" />
          </div>
          <div class="form-row">
            <label class="form-label">Clock Out</label>
            <input type="datetime-local" class="form-input" v-model="editModal.clock_out" />
          </div>
          <div class="form-row">
            <label class="form-label">Status Override</label>
            <select class="form-input" v-model="editModal.status">
              <option value="">— keep auto —</option>
              <option value="present">Present</option>
              <option value="absent">Absent</option>
              <option value="late">Late</option>
              <option value="early_departure">Early Departure</option>
              <option value="half_day">Half Day</option>
              <option value="on_leave">On Leave</option>
              <option value="holiday">Holiday</option>
            </select>
          </div>
          <div class="form-row">
            <label class="form-label">Reason <span class="req">*</span></label>
            <textarea class="form-input" rows="3" v-model="editModal.reason" placeholder="Reason for manual edit…"></textarea>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-outline" @click="editModal.open = false">Cancel</button>
          <button class="btn-primary-action" :disabled="saving" @click="saveEdit">
            <span v-if="saving" class="spinner-border spinner-border-sm me-1"></span>
            {{ saving ? 'Saving…' : 'Save Changes' }}
          </button>
        </div>
      </div>
    </div>

    <!-- ══ Audit Trail Modal ══ -->
    <div class="modal-overlay" v-if="auditModal.open" @click.self="auditModal.open = false">
      <div class="modal-box modal-box--wide">
        <div class="modal-head">
          <p class="modal-title">Audit Trail</p>
          <button class="modal-close" @click="auditModal.open = false"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="modal-body">
          <div v-if="!auditModal.history.length" class="empty-cell">No edits recorded</div>
          <table v-else class="data-table">
            <thead><tr><th>Field</th><th>Old</th><th>New</th><th>Editor</th><th>Reason</th><th>When</th></tr></thead>
            <tbody>
              <tr v-for="h in auditModal.history" :key="h.id">
                <td>{{ h.field_changed }}</td>
                <td class="old-val">{{ h.old_value || '—' }}</td>
                <td class="new-val">{{ h.new_value || '—' }}</td>
                <td>{{ h.editor?.name || '—' }}</td>
                <td>{{ h.reason || '—' }}</td>
                <td>{{ fmtDateTime(h.edited_at) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- ══ Mark Absent Modal ══ -->
    <div class="modal-overlay" v-if="showMarkAbsentModal" @click.self="showMarkAbsentModal = false">
      <div class="modal-box modal-box--absent">
        <div class="modal-head">
          <p class="modal-title">Mark Employees Absent</p>
          <button class="modal-close" @click="showMarkAbsentModal = false"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="modal-body modal-body--search">
          <div class="form-row">
            <label class="form-label">Date <span class="req">*</span></label>
            <input type="date" class="form-input" v-model="absentForm.date" />
          </div>
          <div class="form-row">
            <label class="form-label">
              Employees
              <span v-if="absentSelectedEmployees.length" class="absent-count">{{ absentSelectedEmployees.length }} selected</span>
            </label>
            <search-select
              :key="absentSearchKey"
              v-model="absentSearchId"
              url="/payroll/employees/active"
              query-param="search"
              label-key="full_name"
              sublabel-key="employee_no"
              value-key="id"
              response-key="data"
              placeholder="Search employee by name or number…"
              search-placeholder="Type to search…"
              :min-chars="1"
              @select="onAbsentEmployeeSelect"
            />
            <div v-if="absentSelectedEmployees.length" class="emp-chips">
              <div v-for="e in absentSelectedEmployees" :key="e.id" class="emp-chip">
                <span class="chip-avatar">{{ (e.full_name || '?')[0].toUpperCase() }}</span>
                <div class="chip-info">
                  <span class="chip-name">{{ e.full_name }}</span>
                  <span class="chip-no">{{ e.employee_no }}</span>
                </div>
                <button class="chip-remove" @click="removeAbsentEmployee(e.id)" title="Remove">
                  <font-awesome-icon icon="times" />
                </button>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-outline" @click="showMarkAbsentModal = false">Cancel</button>
          <button class="btn-primary-action" :disabled="saving || !absentSelectedEmployees.length" @click="markAbsent">
            {{ saving ? 'Saving…' : 'Mark Absent' }}
          </button>
        </div>
      </div>
    </div>

    <!-- ══ Column picker modal ══ -->
    <transition name="fade">
      <div v-if="showExportModal" class="col-picker-backdrop" @click.self="showExportModal = false">
        <div class="col-picker-modal">
          <div class="col-picker-header">
            <div>
              <h3 class="col-picker-title">Select Columns</h3>
              <p class="col-picker-sub">Choose what to include in your {{ pendingExportFmt.toUpperCase() }} export</p>
            </div>
            <button class="col-picker-close" @click="showExportModal = false"><font-awesome-icon icon="times" /></button>
          </div>
          <div class="col-picker-actions">
            <button class="col-picker-link" @click="selectedExportCols = exportColumnDefs.map(c => c.key)">Select all</button>
            <span class="col-picker-sep">·</span>
            <button class="col-picker-link" @click="selectedExportCols = []">Deselect all</button>
            <span class="col-picker-count">{{ selectedExportCols.length }} of {{ exportColumnDefs.length }} selected</span>
          </div>
          <div class="col-picker-grid">
            <label v-for="col in exportColumnDefs" :key="col.key" class="col-picker-item">
              <input type="checkbox" :value="col.key" v-model="selectedExportCols" class="col-picker-check" />
              <span class="col-picker-label">{{ col.label }}</span>
            </label>
          </div>
          <div class="col-picker-footer">
            <button class="btn-outline" @click="showExportModal = false">Cancel</button>
            <button class="btn-primary-action" :disabled="selectedExportCols.length === 0 || exportLoading" @click="doExport()">
              <font-awesome-icon :icon="exportLoading ? 'spinner' : pendingExportFmt === 'pdf' ? 'file-pdf' : 'file-csv'" :spin="exportLoading" />
              Export {{ pendingExportFmt.toUpperCase() }}
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- ══ Summary Modal ══ -->
    <div class="modal-overlay" v-if="summaryModal.open" @click.self="summaryModal.open = false">
      <div class="modal-box modal-box--wide">
        <div class="modal-head">
          <p class="modal-title">Attendance Summary</p>
          <button class="modal-close" @click="summaryModal.open = false"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="modal-body">
          <div class="filter-bar mb-16">
            <input type="date" class="filter-input" v-model="summaryModal.from" />
            <input type="date" class="filter-input" v-model="summaryModal.to" />
            <button class="btn-primary-action" @click="fetchSummary">Apply</button>
          </div>
          <div v-if="summaryModal.loading" class="empty-cell">Loading…</div>
          <table v-else class="data-table">
            <thead>
              <tr>
                <th>Employee</th>
                <th>Present</th>
                <th>Late</th>
                <th>Absent</th>
                <th>On Leave</th>
                <th>Holiday</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in summaryModal.rows" :key="row.employee_id">
                <td>{{ row.employee_name }}<br><span class="emp-no">{{ row.employee_no }}</span></td>
                <td>{{ row.present || 0 }}</td>
                <td>{{ row.late || 0 }}</td>
                <td>{{ row.absent || 0 }}</td>
                <td>{{ row.on_leave || 0 }}</td>
                <td>{{ row.holiday || 0 }}</td>
              </tr>
              <tr v-if="!summaryModal.rows.length"><td colspan="6" class="empty-cell">No data</td></tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import axios from 'axios';
import SearchSelect from '../../../../components/SearchSelect.vue';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

export default {
  name: 'AttendancePage',
  components: { SearchSelect },
  data() {
    return {
      loading: false,
      saving: false,
      logs: [],
      meta: { current_page: 1, last_page: 1 },
      filters: { date_from: '', date_to: '', status: '', search: '', length: 5, page: 1 },
      pagination: null,
      editModal: { open: false, id: null, clock_in: '', clock_out: '', status: '', reason: '' },
      auditModal: { open: false, history: [] },
      showMarkAbsentModal: false,
      absentForm: { date: new Date().toISOString().slice(0, 10) },
      absentSelectedEmployees: [],
      absentSearchId: null,
      absentSearchKey: 0,
      summaryModal: { open: false, loading: false, from: '', to: '', rows: [] },
      exportOpen:        false,
      showExportModal:   false,
      pendingExportFmt:  'csv',
      selectedExportCols: [],
      exportLoading:     false,
      exportColumnDefs: [
        { key: 'employee_no',    label: 'Emp No',    pdfWidth: 65 },
        { key: 'employee_name',  label: 'Employee',  pdfWidth: 110 },
        { key: 'work_date',      label: 'Date',      pdfWidth: 90 },
        { key: 'clock_in',       label: 'Clock In',  pdfWidth: 80 },
        { key: 'clock_out',      label: 'Clock Out', pdfWidth: 80 },
        { key: 'worked_hours',   label: 'Hours',     pdfWidth: 60 },
        { key: 'overtime_hours', label: 'Overtime',  pdfWidth: 70 },
        { key: 'status',         label: 'Status',    pdfWidth: 80 },
      ],
    };
  },
  computed: {
    pageRange() {
      if (!this.pagination) return [];
      const cur = this.pagination.current_page, last = this.pagination.last_page;
      const pages = [];
      for (let p = Math.max(1, cur - 2); p <= Math.min(last, cur + 2); p++) pages.push(p);
      return pages;
    },
  },

  mounted() {
    const today = new Date().toISOString().slice(0, 10);
    this.filters.date_from = today;
    this.filters.date_to = today;
    this.summaryModal.from = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().slice(0, 10);
    this.summaryModal.to = today;
    this.fetchAttendance();
  },
  methods: {
    async fetchAttendance() {
      this.loading = true;
      try {
        const { data } = await axios.get('/hr/attendance', { params: this.filters });
        this.pagination = data.data;
        this.logs = data.data?.data || [];
        this.meta = { current_page: data.data?.current_page || 1, last_page: data.data?.last_page || 1 };
      } catch (e) { this.$toast?.error(e.response?.data?.message || 'Failed to load attendance'); }
      finally { this.loading = false; }
    },
    goPage(p) { this.filters.page = p; this.fetchAttendance(); },
    openEdit(log) {
      this.editModal = {
        open: true, id: log.id,
        clock_in: log.clock_in ? log.clock_in.replace(' ', 'T').slice(0, 16) : '',
        clock_out: log.clock_out ? log.clock_out.replace(' ', 'T').slice(0, 16) : '',
        status: '',
        reason: '',
      };
    },
    async saveEdit() {
      if (!this.editModal.reason) { this.$toast?.error('Reason is required'); return; }
      this.saving = true;
      try {
        await axios.put(`/hr/attendance/${this.editModal.id}/edit`, {
          clock_in: this.editModal.clock_in || undefined,
          clock_out: this.editModal.clock_out || undefined,
          status: this.editModal.status || undefined,
          reason: this.editModal.reason,
        });
        this.editModal.open = false;
        this.$toast?.success('Attendance updated');
        this.fetchAttendance();
      } catch (e) { this.$toast?.error(e.response?.data?.message || 'Save failed'); }
      finally { this.saving = false; }
    },
    async openDetail(log) {
      try {
        const { data } = await axios.get(`/hr/attendance/${log.id}`);
        this.auditModal = { open: true, history: data.data?.edit_history || [] };
      } catch { this.auditModal = { open: true, history: [] }; }
    },
    onAbsentEmployeeSelect(opt) {
      if (!opt) return;
      if (this.absentSelectedEmployees.find(e => e.id === opt.id)) {
        this.$toast?.error('Employee already added'); return;
      }
      this.absentSelectedEmployees.push({
        id: opt.id,
        full_name: opt.full_name || `${opt.first_name || ''} ${opt.last_name || ''}`.trim(),
        employee_no: opt.employee_no,
      });
      this.absentSearchId = null;
      this.absentSearchKey++;
    },
    removeAbsentEmployee(id) {
      this.absentSelectedEmployees = this.absentSelectedEmployees.filter(e => e.id !== id);
    },
    async markAbsent() {
      if (!this.absentSelectedEmployees.length) { this.$toast?.error('Select at least one employee'); return; }
      this.saving = true;
      try {
        await axios.post('/hr/attendance/mark-absent', {
          date:         this.absentForm.date,
          employee_ids: this.absentSelectedEmployees.map(e => e.id),
        });
        this.showMarkAbsentModal = false;
        this.absentSelectedEmployees = [];
        this.absentSearchKey++;
        this.$toast?.success('Marked absent');
        this.fetchAttendance();
      } catch (e) { this.$toast?.error(e.response?.data?.message || 'Failed'); }
      finally { this.saving = false; }
    },
    openSummary() { this.summaryModal.open = true; this.fetchSummary(); },
    async fetchSummary() {
      this.summaryModal.loading = true;
      try {
        const { data } = await axios.get('/hr/attendance/summary', {
          params: { date_from: this.summaryModal.from, date_to: this.summaryModal.to },
        });
        this.summaryModal.rows = data.data || [];
      } catch (err){
        this.$toast?.error(err.response?.data?.message || 'failed fetching summary');
      }
      finally { this.summaryModal.loading = false; }
    },
    statusClass(s) {
      return {
        present: 'chip--green', late: 'chip--amber', absent: 'chip--red',
        early_departure: 'chip--orange', half_day: 'chip--purple',
        on_leave: 'chip--blue', holiday: 'chip--gray',
      }[s] || 'chip--gray';
    },
    statusLabel(s) {
      return ({ present: 'Present', late: 'Late', absent: 'Absent', early_departure: 'Early Out',
                half_day: 'Half Day', on_leave: 'On Leave', holiday: 'Holiday' })[s] || s;
    },
    fmtDate(d) { return d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'; },
    fmtTime(dt) {
      if (!dt) return '';
      return new Date(dt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    },
    fmtDateTime(dt) { return dt ? new Date(dt).toLocaleString('en-GB') : '—'; },

    openExportModal(fmt) {
      this.pendingExportFmt  = fmt;
      this.selectedExportCols = this.exportColumnDefs.map(c => c.key);
      this.showExportModal   = true;
    },
    getAttVal(log, key) {
      switch (key) {
        case 'employee_no':    return log.employee?.employee_no ?? '—';
        case 'employee_name':  return `${log.employee?.first_name ?? ''} ${log.employee?.last_name ?? ''}`.trim() || '—';
        case 'work_date':      return this.fmtDate(log.work_date);
        case 'clock_in':       return this.fmtTime(log.clock_in) || '—';
        case 'clock_out':      return this.fmtTime(log.clock_out) || '—';
        case 'worked_hours':   return log.worked_hours != null ? log.worked_hours + 'h' : '—';
        case 'overtime_hours': return log.overtime_hours > 0 ? log.overtime_hours + 'h' : '—';
        case 'status':         return this.statusLabel(log.status);
        default: return '—';
      }
    },
    async doExport() {
      this.exportLoading = true;
      try {
        const params = { ...this.filters, page: 1, length: 10000 };
        const { data } = await axios.get('/hr/attendance', { params });
        const rows = data.data?.data || [];
        const cols = this.exportColumnDefs.filter(c => this.selectedExportCols.includes(c.key));
        if (this.pendingExportFmt === 'csv') {
          const header = cols.map(c => `"${c.label}"`).join(',');
          const body   = rows.map(r => cols.map(c => `"${String(this.getAttVal(r, c.key)).replace(/"/g, '""')}"`).join(',')).join('\n');
          const blob = new Blob(['﻿' + header + '\n' + body], { type: 'text/csv;charset=utf-8;' });
          const url  = URL.createObjectURL(blob);
          const a    = document.createElement('a'); a.href = url; a.download = 'attendance.csv'; a.click(); URL.revokeObjectURL(url);
        } else {
          const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
          const usable = 761;
          const totalW = cols.reduce((s, c) => s + c.pdfWidth, 0);
          const scale  = usable / totalW;
          const colStyles = {};
          cols.forEach((c, i) => { colStyles[i] = { cellWidth: c.pdfWidth * scale }; });
          doc.autoTable({
            head: [cols.map(c => c.label)],
            body: rows.map(r => cols.map(c => this.getAttVal(r, c.key))),
            startY: 40, margin: { left: 20, right: 20 },
            tableWidth: usable,
            styles: { fontSize: 8, cellPadding: 4, overflow: 'ellipsize' },
            headStyles: { fillColor: [0, 74, 173], textColor: 255, fontStyle: 'bold' },
            columnStyles: colStyles,
          });
          doc.save('attendance.pdf');
        }
        this.showExportModal = false;
      } catch (e) { this.$toast?.error(e.response?.data?.message || 'Export failed'); }
      finally { this.exportLoading = false; }
    },
  },
};
</script>

<style scoped>
.page-header  { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:12px; margin-bottom:20px; }
.page-title   { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.page-sub     { font-size:13px; color:#7a90b0; margin:0; }
.page-actions { display:flex; gap:8px; flex-wrap:wrap; }

.filter-bar   { display:flex; align-items:center; flex-wrap:wrap; gap:8px; margin-bottom:16px; }
.filter-input,.filter-select { height:38px; padding:0 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; background:#fff; outline:none; }
.search-input { min-width:180px; }
.mb-16 { margin-bottom:16px; }

.btn-primary-action { display:inline-flex; align-items:center; gap:6px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:0 18px; height:38px; border-radius:8px; border:none; cursor:pointer; white-space:nowrap; }
.btn-primary-action:hover:not(:disabled) { background:#003b8e; }
.btn-primary-action:disabled { opacity:.6; cursor:not-allowed; }
.btn-outline { display:inline-flex; align-items:center; gap:6px; background:#fff; color:#1a2b45; font-size:13px; font-weight:600; padding:0 16px; height:38px; border-radius:8px; border:1px solid #dde5f0; cursor:pointer; }
.btn-outline:hover { background:#f5f7fa; }
.btn-icon { background:none; border:none; cursor:pointer; color:#7a90b0; padding:4px 8px; border-radius:6px; font-size:13px; transition:color .15s,background .15s; }
.btn-icon:hover { background:#f0f4f8; color:#004AAD; }

.table-card { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; }
.data-table  { width:100%; border-collapse:collapse; font-size:13px; }
.data-table thead th { padding:12px 14px; text-align:left; font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.04em; color:#7a90b0; background:#f8fafc; border-bottom:1px solid #e4eaf2; }
.data-table tbody tr { border-bottom:1px solid #f0f4f8; transition:background .1s; }
.data-table tbody tr:hover { background:#f8fafc; }
.data-table tbody td { padding:12px 14px; color:#1a2b45; vertical-align:middle; }
.empty-cell  { text-align:center; color:#aab8cc; padding:40px !important; }
.old-val { color:#ef4444; } .new-val { color:#16a34a; }

.emp-cell  { display:flex; flex-direction:column; gap:1px; }
.emp-name  { font-weight:600; font-size:13px; color:#1a2b45; }
.emp-no    { font-size:11px; color:#7a90b0; }

.status-chip { display:inline-block; padding:3px 10px; border-radius:20px; font-size:11px; font-weight:600; }
.chip--green  { background:#dcfce7; color:#16a34a; }
.chip--amber  { background:#fef9c3; color:#ca8a04; }
.chip--red    { background:#fee2e2; color:#dc2626; }
.chip--orange { background:#ffedd5; color:#ea580c; }
.chip--purple { background:#f3e8ff; color:#7c3aed; }
.chip--blue   { background:#dbeafe; color:#1d4ed8; }
.chip--gray   { background:#f1f5f9; color:#64748b; }

.pagination  { display:flex; align-items:center; justify-content:center; gap:12px; padding:14px; border-top:1px solid #f0f4f8; }
.page-btn    { background:#fff; border:1px solid #dde5f0; border-radius:6px; padding:4px 12px; font-size:15px; cursor:pointer; color:#1a2b45; }
.page-btn:disabled { opacity:.4; cursor:not-allowed; }
.page-info   { font-size:13px; color:#7a90b0; }

/* Skeleton */
.sk-table { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; }
.sk-row-head,.sk-row { height:44px; background:linear-gradient(90deg,#f0f4f8 25%,#e4eaf2 50%,#f0f4f8 75%); background-size:200% 100%; animation:shimmer 1.4s infinite; margin-bottom:1px; }
.sk-row-head { height:40px; background:#f8fafc; }
@keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }

/* Modal */
.modal-overlay { position:fixed; inset:0; background:rgba(0,0,0,.45); z-index:1000; display:flex; align-items:center; justify-content:center; padding:16px; }
.modal-box      { background:#fff; border-radius:14px; width:100%; max-width:520px; max-height:90vh; overflow-y:auto; box-shadow:0 20px 60px rgba(0,0,0,.2); }
.modal-box--wide { max-width:860px; }
.modal-head     { display:flex; align-items:center; justify-content:space-between; padding:18px 22px; border-bottom:1px solid #f0f4f8; }
.modal-title    { font-size:15px; font-weight:700; color:#1a2b45; margin:0; }
.modal-close    { background:none; border:none; cursor:pointer; color:#7a90b0; font-size:16px; padding:2px 6px; }
.modal-body     { padding:20px 22px; display:flex; flex-direction:column; gap:14px; }
.modal-footer   { display:flex; justify-content:flex-end; gap:8px; padding:16px 22px; border-top:1px solid #f0f4f8; }

.form-row   { display:flex; flex-direction:column; gap:5px; }
.form-label { font-size:12px; font-weight:600; color:#4a5e78; }
.form-input { width:100%; padding:9px 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; outline:none; box-sizing:border-box; background:#fff; }
.form-input:focus { border-color:#004AAD; }
.field-hint { font-size:11px; color:#7a90b0; margin:4px 0 0; }
.req { color:#dc2626; }
.me-1 { margin-right:4px; }

.modal-box--absent { min-height:420px; }
.modal-body--search { padding-bottom:240px; }

.absent-count { margin-left:8px; font-size:11px; font-weight:700; padding:2px 8px; border-radius:20px; background:#fef9c3; color:#ca8a04; }

.emp-chips { display:flex; flex-direction:column; gap:6px; margin-top:8px; }
.emp-chip  { display:flex; align-items:center; gap:10px; padding:8px 10px; background:#f8fafc; border:1px solid #e4eaf2; border-radius:8px; }
.chip-avatar { width:30px; height:30px; border-radius:50%; background:#004AAD; color:#fff; font-size:12px; font-weight:700; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
.chip-info   { flex:1; min-width:0; display:flex; flex-direction:column; gap:1px; }
.chip-name   { font-size:13px; font-weight:600; color:#1a2b45; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.chip-no     { font-size:11px; color:#7a90b0; }
.chip-remove { background:none; border:none; cursor:pointer; color:#aab8cc; font-size:12px; padding:4px; border-radius:4px; flex-shrink:0; }
.chip-remove:hover { color:#dc2626; background:#fef2f2; }

/* ── Export column picker ─────────────────────────────── */
.export-wrap { position:relative; display:inline-flex; }
.btn-export  { display:inline-flex; align-items:center; gap:6px; background:#fff; color:#1a2b45; font-size:13px; font-weight:600; padding:0 16px; height:38px; border-radius:8px; border:1px solid #dde5f0; cursor:pointer; }
.btn-export:hover { background:#f5f7fa; }
.export-menu { position:absolute; top:calc(100% + 4px); right:0; background:#fff; border:1px solid #dde5f0; border-radius:10px; box-shadow:0 8px 24px rgba(0,0,0,.1); z-index:200; min-width:160px; padding:6px; }
.export-item { display:flex; align-items:center; gap:8px; width:100%; padding:8px 12px; border:none; background:none; font-size:13px; color:#1a2b45; cursor:pointer; border-radius:6px; }
.export-item:hover { background:#f0f7ff; color:#004AAD; }
.col-picker-backdrop { position:fixed; inset:0; background:rgba(0,0,0,.45); z-index:1200; display:flex; align-items:center; justify-content:center; padding:16px; }
.col-picker-modal  { background:#fff; border-radius:16px; width:100%; max-width:520px; max-height:90vh; overflow-y:auto; box-shadow:0 24px 64px rgba(0,0,0,.18); }
.col-picker-header { display:flex; align-items:flex-start; justify-content:space-between; padding:20px 22px 0; }
.col-picker-title  { font-size:15px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.col-picker-sub    { font-size:12px; color:#7a90b0; margin:0; }
.col-picker-close  { background:none; border:none; cursor:pointer; color:#7a90b0; font-size:16px; padding:4px 6px; flex-shrink:0; }
.col-picker-actions { display:flex; align-items:center; gap:8px; padding:14px 22px 0; }
.col-picker-link   { background:none; border:none; cursor:pointer; color:#004AAD; font-size:12px; font-weight:600; padding:0; }
.col-picker-sep    { color:#dde5f0; }
.col-picker-count  { margin-left:auto; font-size:12px; color:#7a90b0; }
.col-picker-grid   { display:grid; grid-template-columns:1fr 1fr; gap:4px; padding:14px 22px; }
.col-picker-item   { display:flex; align-items:center; gap:8px; padding:8px 10px; border-radius:8px; cursor:pointer; border:1px solid #f0f4f8; }
.col-picker-item:hover { background:#f0f7ff; border-color:#c7d8ef; }
.col-picker-check  { cursor:pointer; accent-color:#004AAD; width:15px; height:15px; flex-shrink:0; }
.col-picker-label  { font-size:13px; color:#1a2b45; font-weight:500; }
.col-picker-footer { display:flex; justify-content:flex-end; gap:8px; padding:14px 22px 20px; border-top:1px solid #f0f4f8; margin-top:8px; }
.fade-enter-active,.fade-leave-active { transition:opacity .18s; }
.fade-enter,.fade-leave-to { opacity:0; }

/* ── Per-page + Pagination ─────────────────────────────── */
.per-page-wrap    { display:flex; align-items:center; gap:6px; }
.per-page-label   { font-size:12px; color:#7a90b0; white-space:nowrap; }
.per-page-select  { height:38px; padding:0 10px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; background:#fff; outline:none; }
.pagination-bar   { display:flex; align-items:center; justify-content:space-between; flex-wrap:wrap; gap:10px; padding:14px 16px; border-top:1px solid #f0f4f8; }
.pagination-info  { font-size:13px; color:#7a90b0; }
.pagination-controls { display:flex; align-items:center; gap:4px; }
.pg-btn  { background:#fff; border:1px solid #dde5f0; border-radius:6px; width:32px; height:32px; font-size:13px; cursor:pointer; display:flex; align-items:center; justify-content:center; color:#1a2b45; }
.pg-btn:hover:not(:disabled) { background:#f0f7ff; border-color:#004AAD; color:#004AAD; }
.pg-btn:disabled { opacity:.4; cursor:not-allowed; }
.pg-pages { display:flex; align-items:center; gap:2px; }
.pg-num   { background:#fff; border:1px solid #dde5f0; border-radius:6px; width:32px; height:32px; font-size:13px; cursor:pointer; display:flex; align-items:center; justify-content:center; color:#1a2b45; }
.pg-num:hover    { background:#f0f7ff; border-color:#004AAD; color:#004AAD; }
.pg-num--active  { background:#004AAD; border-color:#004AAD; color:#fff; font-weight:700; }
</style>
